import type { Context, ReactNode } from 'react'
import { useEffect } from 'react'
import { createContext, useContext, useState } from 'react'

export type FormData<T = unknown> = T

export type FormContextProps<T = unknown> = {
  data: FormData<T>
  setFormData: (fieldName: keyof T, fieldValue: unknown, isSetByUser?: boolean) => void
  resetForm: () => void
}

export type FormProps<T = unknown> = {
  children: ReactNode
  formFields: T
}

const INITIAL_STATE = {
  data: {} as FormData,
  setFormData: () => undefined,
  resetForm: () => undefined
}

const FormContext = createContext<FormContextProps>(INITIAL_STATE)

function FormContextProvider<T>({ children, formFields }: FormProps<T>) {
  const [data, setData] = useState<T>(formFields)

  useEffect(() => {
    setData(formFields)
  }, [formFields])

  const resetForm = () => setData(formFields)

  return (
    <FormContext.Provider
      value={{
        data,
        setFormData: (fieldName: keyof T, fieldValue: unknown, isSetByUser?: boolean) =>
          setData((current) => ({ ...current, [fieldName]: { value: fieldValue, isSetByUser } })),
        resetForm
      }}>
      {children}
    </FormContext.Provider>
  )
}

export function useForm<T>() {
  return useContext(FormContext as Context<FormContextProps<T>>)
}

export default FormContextProvider
